import { useEffect, useState } from "react";
import { Menu, X, ArrowUpRight } from "lucide-react";
import Logo from "@/components/site/Logo";

const NAV = [
    { label: "Platform", href: "#platform" },
    { label: "SmartCaddy", href: "#smartcaddy" },
    { label: "Compare", href: "#compare" },
    { label: "Company", href: "#company" },
    { label: "Deploy", href: "#deploy" },
    { label: "Visit Store", href: "https://smartcaddy.store/", external: true },
];

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="site-navbar"
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "backdrop-blur-xl bg-black/70 border-b border-white/10"
                    : "bg-transparent border-b border-transparent"
            }`}
        >
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-[68px] flex items-center justify-between">
                <a
                    href="#top"
                    data-testid="nav-logo"
                    className="flex items-center group"
                    aria-label="ReTechler home"
                >
                    <Logo height={34} className="transition-opacity group-hover:opacity-80" />
                </a>

                <nav className="hidden lg:flex items-center gap-9">
                    {NAV.map((n) => (
                        <a
                            key={n.href}
                            href={n.href}
                            target={n.external ? "_blank" : undefined}
                            rel={n.external ? "noopener noreferrer" : undefined}
                            data-testid={`nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className={`text-sm transition-colors font-medium ${
                                n.external
                                    ? "text-rt-teal hover:text-white inline-flex items-center gap-1"
                                    : "text-white/70 hover:text-white"
                            }`}
                        >
                            {n.label}
                            {n.external && <ArrowUpRight className="w-3.5 h-3.5" />}
                        </a>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <a
                        href="#contact"
                        data-testid="nav-cta-demo"
                        className="hidden sm:inline-flex items-center gap-1.5 bg-rt-teal text-black font-medium px-5 py-2.5 text-sm hover:bg-rt-tealDim transition-colors"
                    >
                        Request Demo <ArrowUpRight className="w-4 h-4" />
                    </a>
                    <button
                        data-testid="nav-mobile-toggle"
                        className="lg:hidden text-white p-2"
                        onClick={() => setOpen((s) => !s)}
                        aria-label="Toggle navigation"
                    >
                        {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                    </button>
                </div>
            </div>

            {open && (
                <div className="lg:hidden border-t border-white/10 bg-black/95 backdrop-blur-xl">
                    <div className="px-6 py-6 flex flex-col gap-4">
                        {NAV.map((n) => (
                            <a
                                key={n.href}
                                href={n.href}
                                target={n.external ? "_blank" : undefined}
                                rel={n.external ? "noopener noreferrer" : undefined}
                                onClick={() => setOpen(false)}
                                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className={`text-base ${n.external ? "text-rt-teal" : "text-white/80"}`}
                            >
                                {n.label}
                            </a>
                        ))}
                        <a
                            href="#contact"
                            onClick={() => setOpen(false)}
                            data-testid="mobile-cta-demo"
                            className="mt-2 inline-flex items-center justify-center bg-rt-teal text-black font-medium px-5 py-3 text-sm"
                        >
                            Request Demo
                        </a>
                    </div>
                </div>
            )}
        </header>
    );
}
